/**
 * AdminCRMContactDetail — Single contact view.
 *
 * Sections:
 *   • Header (name, email, phone, organisation, roles, DNC toggle).
 *   • Linked records (delegates / students / donations…).
 *   • Timeline (activities, notes, tasks, opportunities).
 *   • Quick add: note · task.
 *
 * Route: /admin/crm/contacts/:id
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Mail, Phone, Building2, Ban, ArrowLeft, Plus, StickyNote, ListChecks,
  Activity, CheckCircle2, MessageSquare, TrendingUp, Sparkles, Copy, Send,
} from 'lucide-react';
import { getToken } from '@/lib/authToken';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

const AdminCRMContactDetail = () => {
  const { id } = useParams();
  const qc = useQueryClient();
  const [noteText, setNoteText] = useState('');
  const [taskTitle, setTaskTitle] = useState('');

  const { data: contact } = useQuery({
    queryKey: ['crm-contact', id],
    queryFn: async () => (await axios.get(`${API}/api/crm/contacts/${id}`, auth())).data,
  });

  const { data: timeline } = useQuery({
    queryKey: ['crm-timeline', id],
    queryFn: async () => (await axios.get(`${API}/api/crm/contacts/${id}/timeline`, auth())).data,
    enabled: !!contact,
  });

  const toggleDNC = useMutation({
    mutationFn: async () => (await axios.post(`${API}/api/crm/contacts/${id}/dnc`, null, auth())).data,
    onSuccess: (d) => {
      toast.success(d.do_not_contact ? 'Marked do-not-contact' : 'Do-not-contact cleared');
      qc.invalidateQueries({ queryKey: ['crm-contact', id] });
    },
  });

  const addNote = useMutation({
    mutationFn: async () => {
      await axios.post(`${API}/api/crm/notes`, {
        tenant_id: contact.tenant_id, body: noteText, contact_id: id,
      }, auth());
    },
    onSuccess: () => {
      setNoteText('');
      toast.success('Note added');
      qc.invalidateQueries({ queryKey: ['crm-timeline', id] });
    },
  });

  const addTask = useMutation({
    mutationFn: async () => {
      await axios.post(`${API}/api/crm/tasks`, {
        tenant_id: contact.tenant_id, title: taskTitle, contact_id: id,
      }, auth());
    },
    onSuccess: () => {
      setTaskTitle('');
      toast.success('Task created');
      qc.invalidateQueries({ queryKey: ['crm-timeline', id] });
    },
  });

  const completeTask = useMutation({
    mutationFn: async (taskId) => {
      await axios.post(`${API}/api/crm/tasks/${taskId}/complete`, null, auth());
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['crm-timeline', id] }),
  });

  // ----- CRM Copilot -----
  const [copilotStatus, setCopilotStatus] = useState(null);
  const [summary, setSummary] = useState(null);
  const [draft, setDraft] = useState(null);
  const [copilotBusy, setCopilotBusy] = useState(null); // 'summarise' | 'draft' | null
  const [copilotErr, setCopilotErr] = useState(null);
  const [intent, setIntent] = useState('Re-engage after a long silence');
  const [tone, setTone] = useState('warm-professional');
  const [channel, setChannel] = useState('email');

  useEffect(() => {
    axios.get(`${API}/api/crm/copilot/status`, auth())
      .then(r => setCopilotStatus(r.data))
      .catch(() => setCopilotStatus({ configured: false }));
  }, []);

  const runCopilot = async (kind, opts = {}) => {
    setCopilotBusy(kind);
    setCopilotErr(null);
    try {
      if (kind === 'summarise') {
        const r = await axios.post(`${API}/api/crm/copilot/summarise-contact`,
          { contact_id: id, force_refresh: !!opts.force }, auth());
        setSummary(r.data);
      } else {
        const r = await axios.post(`${API}/api/crm/copilot/draft-follow-up`,
          { contact_id: id, intent, tone, channel }, auth());
        setDraft(r.data);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setCopilotErr(detail);
      toast.error(detail);
    } finally {
      setCopilotBusy(null);
    }
  };

  const copyDraft = () => {
    if (!draft) return;
    const text = [draft.subject ? `Subject: ${draft.subject}` : '', draft.body]
      .filter(Boolean).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  if (!contact) {
    return <div className="p-6 text-slate-400">Loading contact…</div>;
  }

  const activities = timeline?.activities || [];
  const notes = timeline?.notes || [];
  const tasks = timeline?.tasks || [];
  const opportunities = timeline?.opportunities || [];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 p-6">
      <div className="max-w-5xl mx-auto space-y-5">
        <Link to="/admin/crm" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
          <ArrowLeft className="h-3 w-3" /> Back to CRM
        </Link>

        {/* Header */}
        <Card className="bg-slate-900/60 border-slate-800" data-testid="crm-contact-header">
          <CardContent className="p-5">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h1 className="text-2xl font-semibold text-white">{contact.full_name || '—'}</h1>
                <div className="flex gap-3 mt-2 text-sm text-slate-400 flex-wrap">
                  {contact.primary_email && (
                    <span className="inline-flex items-center gap-1"><Mail className="h-3 w-3" />{contact.primary_email}</span>
                  )}
                  {contact.primary_phone && (
                    <span className="inline-flex items-center gap-1"><Phone className="h-3 w-3" />{contact.primary_phone}</span>
                  )}
                  {contact.organisation && (
                    <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{contact.organisation}</span>
                  )}
                </div>
                <div className="flex gap-1 mt-3 flex-wrap">
                  {(contact.roles || []).map(r => (
                    <Badge key={r} variant="outline" className="text-[10px]">{r}</Badge>
                  ))}
                  {contact.do_not_contact && <Badge variant="destructive" className="text-[10px]">DNC</Badge>}
                </div>
              </div>
              <div className="flex gap-2 items-start">
                <OutreachButtons contact={contact} />
                <Button variant={contact.do_not_contact ? 'default' : 'outline'} size="sm"
                         onClick={() => toggleDNC.mutate()}
                         data-testid="crm-toggle-dnc">
                  <Ban className="h-4 w-4 mr-1" />
                  {contact.do_not_contact ? 'Restore contact' : 'Mark DNC'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid lg:grid-cols-3 gap-5">
          {/* Linked records */}
          <Card className="bg-slate-900/60 border-slate-800 lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-sm">Linked records</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-xs">
              {(contact.linked_records || []).map((lk, idx) => (
                <div key={idx} className="border border-slate-800 rounded-md p-2 bg-slate-800/30">
                  <div className="text-slate-300 capitalize">{lk.type}</div>
                  <div className="text-slate-500">{lk.collection}</div>
                  <div className="text-slate-600 truncate">{lk.id}</div>
                </div>
              ))}
              {!contact.linked_records?.length && (
                <p className="text-slate-500">No linked records.</p>
              )}
            </CardContent>
          </Card>

          {/* Opportunities + Notes + Tasks + Activity */}
          <div className="lg:col-span-2 space-y-5">
            {/* AI Copilot */}
            {copilotStatus?.configured && (
              <Card className="bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5 border-indigo-400/30" data-testid="crm-copilot-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-slate-100 flex items-center justify-between">
                    <span className="inline-flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-indigo-300" /> CRM Copilot
                    </span>
                    <span className="text-[10px] text-slate-500 font-normal">
                      {copilotStatus.model}
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline"
                            onClick={() => runCopilot('summarise')}
                            disabled={copilotBusy === 'summarise'}
                            data-testid="copilot-summarise-btn">
                      {copilotBusy === 'summarise'
                        ? 'Summarising…'
                        : <><Sparkles className="h-3.5 w-3.5 mr-1" />Summarise relationship</>}
                    </Button>
                    <Button size="sm" variant="outline"
                            onClick={() => runCopilot('draft')}
                            disabled={copilotBusy === 'draft' || contact.do_not_contact}
                            data-testid="copilot-draft-btn">
                      {copilotBusy === 'draft'
                        ? 'Drafting…'
                        : <><Mail className="h-3.5 w-3.5 mr-1" />Draft follow-up</>}
                    </Button>
                  </div>

                  {/* Draft options */}
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Input value={intent} onChange={e => setIntent(e.target.value)}
                           placeholder="Intent (e.g. invite to conference)"
                           className="bg-slate-900 border-slate-800 text-xs"
                           data-testid="copilot-intent" />
                    <Input value={tone} onChange={e => setTone(e.target.value)}
                           placeholder="Tone"
                           className="bg-slate-900 border-slate-800 text-xs" />
                    <Input value={channel} onChange={e => setChannel(e.target.value)}
                           placeholder="email | sms | whatsapp"
                           className="bg-slate-900 border-slate-800 text-xs" />
                  </div>

                  {copilotErr && (
                    <div className="text-xs text-rose-300 bg-rose-500/10 border border-rose-500/30 rounded-md p-2"
                         data-testid="copilot-error">
                      {copilotErr}
                    </div>
                  )}

                  {summary && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 space-y-2"
                         data-testid="copilot-summary">
                      <div className="flex items-center justify-between">
                        <div className="text-xs uppercase text-slate-500">Summary</div>
                        <div className="flex items-center gap-2">
                          {summary.cached && (
                            <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400"
                                   data-testid="copilot-cached-badge">
                              cached · {summary.cache_age_minutes ?? 0}m ago
                            </Badge>
                          )}
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]"
                                  disabled={copilotBusy === 'summarise'}
                                  onClick={() => runCopilot('summarise', { force: true })}
                                  data-testid="copilot-refresh-btn">
                            <Sparkles className="h-3 w-3 mr-1" />Refresh
                          </Button>
                        </div>
                      </div>
                      <p className="text-sm text-slate-200">{summary.summary}</p>
                      {summary.talking_points?.length > 0 && (
                        <>
                          <div className="text-xs uppercase text-slate-500 pt-1">Talking points</div>
                          <ul className="text-xs text-slate-300 list-disc pl-4 space-y-0.5">
                            {summary.talking_points.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </>
                      )}
                      {summary.next_best_actions?.length > 0 && (
                        <>
                          <div className="text-xs uppercase text-slate-500 pt-1">Next best actions</div>
                          <ul className="text-xs text-emerald-300 list-disc pl-4 space-y-0.5">
                            {summary.next_best_actions.map((p, i) => <li key={i}>{p}</li>)}
                          </ul>
                        </>
                      )}
                      <div className="text-[10px] text-slate-600">{summary.model} · {summary.latency_ms}ms</div>
                    </div>
                  )}

                  {draft && (
                    <div className="bg-slate-900/60 border border-slate-800 rounded-md p-3 space-y-2"
                         data-testid="copilot-draft">
                      <div className="flex items-center justify-between">
                        <div className="text-xs uppercase text-slate-500">Drafted {channel}</div>
                        <Button size="sm" variant="ghost" onClick={copyDraft}
                                data-testid="copilot-copy-btn">
                          <Copy className="h-3.5 w-3.5 mr-1" />Copy
                        </Button>
                      </div>
                      {draft.subject && (
                        <div className="text-sm text-slate-300">
                          <span className="text-slate-500">Subject:</span> {draft.subject}
                        </div>
                      )}
                      <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans">{draft.body}</pre>
                      <div className="text-[10px] text-slate-600">{draft.model} · {draft.latency_ms}ms</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Opportunities */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4" />Opportunities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2" data-testid="crm-contact-opportunities">
                {opportunities.length === 0 && <p className="text-xs text-slate-500">None yet.</p>}
                {opportunities.map(o => (
                  <div key={o.id} className="border border-slate-800 rounded-md p-3 bg-slate-800/30 text-sm">
                    <div className="text-slate-100">{o.title}</div>
                    <div className="text-xs text-slate-500">
                      ${Number(o.value || 0).toLocaleString()} · status: {o.status}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Notes */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><StickyNote className="h-4 w-4" />Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="crm-contact-notes">
                <div className="flex gap-2">
                  <Textarea placeholder="Add a note…" value={noteText}
                            onChange={e => setNoteText(e.target.value)}
                            className="bg-slate-900 border-slate-800 min-h-[60px]"
                            data-testid="crm-note-input" />
                  <Button onClick={() => addNote.mutate()} disabled={!noteText.trim() || addNote.isPending}
                          data-testid="crm-note-save">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {notes.map(n => (
                  <div key={n.id} className="border border-slate-800 rounded-md p-3 bg-slate-800/30 text-sm">
                    <div className="text-slate-200">{n.body}</div>
                    <div className="text-[10px] text-slate-500 mt-1">
                      {n.author_name} · {new Date(n.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
                {notes.length === 0 && <p className="text-xs text-slate-500">No notes yet.</p>}
              </CardContent>
            </Card>

            {/* Tasks */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><ListChecks className="h-4 w-4" />Tasks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3" data-testid="crm-contact-tasks">
                <div className="flex gap-2">
                  <Input placeholder="New task title" value={taskTitle}
                         onChange={e => setTaskTitle(e.target.value)}
                         className="bg-slate-900 border-slate-800"
                         data-testid="crm-task-input" />
                  <Button onClick={() => addTask.mutate()} disabled={!taskTitle.trim() || addTask.isPending}
                          data-testid="crm-task-save">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-2 border border-slate-800 rounded-md p-2 bg-slate-800/30">
                    <CheckCircle2 className={`h-4 w-4 ${t.status === 'done' ? 'text-emerald-400' : 'text-slate-500'}`} />
                    <div className={`flex-1 text-sm ${t.status === 'done' ? 'line-through text-slate-500' : 'text-slate-200'}`}>
                      {t.title}
                    </div>
                    {t.status !== 'done' && (
                      <Button size="sm" variant="ghost" onClick={() => completeTask.mutate(t.id)}
                              data-testid={`crm-task-complete-${t.id}`}>
                        Done
                      </Button>
                    )}
                  </div>
                ))}
                {tasks.length === 0 && <p className="text-xs text-slate-500">No tasks yet.</p>}
              </CardContent>
            </Card>

            {/* Activity timeline */}
            <Card className="bg-slate-900/60 border-slate-800">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2"><Activity className="h-4 w-4" />Activity</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-xs" data-testid="crm-contact-activity">
                {activities.map(a => (
                  <div key={a.id} className="flex items-start gap-2 pb-2 border-b border-slate-800/60">
                    <MessageSquare className="h-3 w-3 text-slate-500 mt-1 shrink-0" />
                    <div className="flex-1">
                      <div className="text-slate-300 flex items-center gap-2 flex-wrap">
                        <span>{a.message}</span>
                        {a.kind && <Badge variant="outline" className="text-[9px] py-0">{a.kind}</Badge>}
                      </div>
                      <div className="text-slate-600 text-[10px]">
                        {a.actor_name || 'System'} · {new Date(a.created_at).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
                {activities.length === 0 && <p className="text-slate-500">No activity logged yet.</p>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCRMContactDetail;

// ============== Outreach (Email + WhatsApp) ==============
const OutreachButtons = ({ contact }) => {
  const [openKind, setOpenKind] = useState(null); // 'email' | 'whatsapp'
  const dnc = contact.do_not_contact;
  return (
    <>
      <Button variant="outline" size="sm" disabled={dnc || !contact.primary_email}
               onClick={() => setOpenKind('email')}
               className="border-indigo-400/60 text-indigo-200 hover:bg-indigo-500/10"
               data-testid="crm-send-email-btn">
        <Mail className="h-4 w-4 mr-1" /> Email
      </Button>
      <Button variant="outline" size="sm" disabled={dnc || !contact.primary_phone}
               onClick={() => setOpenKind('whatsapp')}
               className="border-emerald-400/60 text-emerald-200 hover:bg-emerald-500/10"
               data-testid="crm-send-whatsapp-btn">
        <MessageSquare className="h-4 w-4 mr-1" /> WhatsApp
      </Button>
      <OutreachDialog kind={openKind} contact={contact}
                       onClose={() => setOpenKind(null)} />
    </>
  );
};

const OutreachDialog = ({ kind, contact, onClose }) => {
  const qc = useQueryClient();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  useEffect(() => { setSubject(''); setBody(''); }, [kind, contact?.id]);

  const send = useMutation({
    mutationFn: async () => {
      if (kind === 'email') {
        return (await axios.post(`${API}/api/crm/contacts/${contact.id}/email`,
          { subject, body_html: body.replace(/\n/g, '<br>'), body_text: body }, auth())).data;
      }
      return (await axios.post(`${API}/api/crm/contacts/${contact.id}/whatsapp`,
        { message: body }, auth())).data;
    },
    onSuccess: () => {
      toast.success(kind === 'email' ? 'Email sent' : 'WhatsApp sent');
      qc.invalidateQueries({ queryKey: ['crm-timeline', contact.id] });
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Send failed'),
  });

  if (!kind) return null;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-200">
        <DialogHeader>
          <DialogTitle>
            {kind === 'email'
              ? <><Mail className="inline h-4 w-4 mr-1.5" /> Email · {contact.primary_email}</>
              : <><MessageSquare className="inline h-4 w-4 mr-1.5" /> WhatsApp · {contact.primary_phone}</>}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Sent from the CRM and logged on the contact timeline.
          </DialogDescription>
        </DialogHeader>
        {kind === 'email' && (
          <Input value={subject} onChange={e => setSubject(e.target.value)}
                 placeholder="Subject" className="bg-slate-800/60 border-slate-700"
                 data-testid="crm-outreach-subject" />
        )}
        <Textarea value={body} onChange={e => setBody(e.target.value)}
                   placeholder={kind === 'email' ? 'Email body…' : 'WhatsApp message…'}
                   rows={kind === 'email' ? 8 : 4}
                   className="bg-slate-800/60 border-slate-700"
                   data-testid="crm-outreach-body" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => send.mutate()}
                   disabled={send.isPending || !body.trim() || (kind === 'email' && !subject.trim())}
                   data-testid="crm-outreach-send">
            <Send className="h-4 w-4 mr-1.5" />
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
